import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import YouTube from 'youtube-iframe';
import Player from '@vimeo/player';

gsap.registerPlugin(ScrollTrigger);

export default class VideoHandler {
	customPlayer() {
		gsap.utils.toArray('[data-video="container"]').forEach((e) => {
			if (!e) return;

			const videoType = e.getAttribute("data-video-type");

			const video = e.querySelector('[data-video-url]');
			if (!video) return;

			const url = video.getAttribute("data-video-url");
			if (!url) return;

			const playButton = e.querySelector('[data-video-play]');
			const muteButton = e.querySelector('[data-video-mute]');
			const playType = e.getAttribute("data-video-play");
			const { type, id, hash } = getVideoTypeAndId(url);
			let player;

			if (type === "youtube") {
				loadYouTubePlayer(id);
			} else if (type === "vimeo") {
				loadVimeoPlayer(id, hash, url);
			} else if (type === "mp4") {
				loadMP4Video(url);
			}


			function getVideoTypeAndId(url) {
				const youtubeRegex = /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/))([\w-]+)/;
				const vimeoRegex = /(?:vimeo\.com\/(?:video\/)?(?:channels\/\w+\/)?(?:groups\/\w+\/videos\/)?(?:album\/\w+\/video\/)?(?:showcase\/\w+\/video\/)?)([\d]+)(?:\/([a-zA-Z0-9]+))?/;
				const mp4Regex = /\.mp4(\?.*)?$/;

				let match;
				if ((match = url.match(youtubeRegex))) {
					return { type: "youtube", id: match[1] };
				} else if ((match = url.match(vimeoRegex))) {
					// For Vimeo, combine video ID and privacy hash if present
					const videoId = match[1];
					const privacyHash = match[2]; // This captures the hash like 'b50ca94c5c'
					return {
						type: "vimeo",
						id: videoId,
						hash: privacyHash || null
					};
				} else if (mp4Regex.test(url)) {
					return { type: "mp4", id: url };
				}
				return { type: "mp4", id: url };
			}

			function loadYouTubePlayer(videoId) {
				require('youtube-iframe').load(function(YT) {
					player = new YT.Player(video, {
						videoId: videoId,
                        host: 'https://www.youtube-nocookie.com',
						playerVars: {
							controls: 0,
							modestbranding: 1,
							rel: 0,
							showinfo: 0,
							iv_load_policy: 3,
							cc_load_policy: 0,
							fs: 0,
							autohide: 1,
							playsinline: 1,
							disablekb: 1
						},
						events: {
							onReady: function (event) {
								// Hide YouTube's play button overlay
								const iframe = event.target.getIframe();
								try {
									const style = document.createElement('style');
									style.textContent = '.ytp-large-play-button, .ytp-cued-thumbnail-overlay { display: none !important; }';
									iframe.contentDocument.head.appendChild(style);
								} catch (e) {
									// Cross-origin restriction - can't access iframe content
									console.log('Cannot hide YouTube controls due to CORS');
								}

								if (playType === "autoplay") {
									event.target.mute();
									setupScrollTrigger(event.target);
								} else {
									if (playButton) {
										setupPlayButton(event.target);
									}
								}

							},
							onStateChange: function(event) {
								if (event.data === YT.PlayerState.PLAYING) {
									const iframe = event.target.getIframe();
									if (iframe.classList.contains('pointer-events-none')) {
										iframe.classList.remove('pointer-events-none');
									}
								}
							}
						},
					});
				});
			}

			function loadVimeoPlayer(videoId, privacyHash, originalUrl) {
				const playerOptions = {
					controls: false,
					muted: playType === "autoplay",
					loop: true,
				};

				// For private/unlisted videos with hash, use the full URL
				// Otherwise use the video ID
				if (privacyHash) {
					playerOptions.url = originalUrl;
				} else {
					playerOptions.id = videoId;
				}

				player = new Player(video, playerOptions);

				player.ready().then(() => {
					if (playType === "autoplay") {
						setupScrollTrigger(player);
					} else {
						if (playButton) {
							setupPlayButton(player);
						}
					}
				});
			}

			function loadMP4Video(videoUrl) {
				const videoElement = document.createElement("video");
				videoElement.src = videoUrl;
				videoElement.controls = false;
				videoElement.classList.add('w-full');
				videoElement.classList.add('h-full');
				videoElement.classList.add('object-cover');
				videoElement.muted = playType === "autoplay";
				videoElement.playsInline = true;
				videoElement.loop = true;

				video.innerHTML = "";
				video.appendChild(videoElement);

				if (playType === "autoplay") {
					setupScrollTrigger(videoElement);
				} else {
					if (playButton) {
						setupPlayButton(videoElement);
					}
				}
			}

			function setupScrollTrigger(videoPlayer) {
				ScrollTrigger.create({
					trigger: e,
					start: "top 100%",
					end: "bottom 0%",
					toggleActions: "play pause resume pause",
					onEnter: () => playVideo(videoPlayer),
				});
				if (e.querySelector('[data-video-poster]')) {
					e.querySelector('[data-video-poster]').classList.add('hidden')
				};
			}

			function setupPlayButton(videoPlayer) {
				if (!playButton) return;

				const playIconSpan = playButton.querySelector('span:first-child');
				const muteIconSpan = muteButton ? muteButton.querySelector('span:first-child') : null;

				// Get theme URI from localized script
				const themeUri = typeof themeData !== 'undefined' ? themeData.theme_uri : '';

				// Load SVG icons
				const playSvg = fetch(`${themeUri}/_dist/images/play.svg`)
					.then(res => res.text())
					.catch(() => 'Play');

				const pauseSvg = fetch(`${themeUri}/_dist/images/pause.svg`)
					.then(res => res.text())
					.catch(() => 'Pause');

				const muteSvg = fetch(`${themeUri}/_dist/images/mute.svg`)
					.then(res => res.text())
					.catch(() => 'Mute');

				const unmuteSvg = fetch(`${themeUri}/_dist/images/unmute.svg`)
					.then(res => res.text())
					.catch(() => 'Unmute');

				// Play/Pause button
				playButton.addEventListener("click", async () => {
					if (await isPlaying(videoPlayer)) {
						pauseVideo(videoPlayer);
						if (playIconSpan) playIconSpan.innerHTML = await playSvg;
					} else {
						playVideo(videoPlayer);
						if (playIconSpan) playIconSpan.innerHTML = await pauseSvg;
						if (e.querySelector('[data-video-poster]')) {
							e.querySelector('[data-video-poster]').classList.add('hidden');
						}
						// Update mute button icon to reflect current mute state
						if (muteIconSpan) {
							const muted = await isMuted(videoPlayer);
							muteIconSpan.innerHTML = muted ? await unmuteSvg : await muteSvg;
						}
					}
				});

				// Mute/Unmute button
				if (muteButton) {
					muteButton.addEventListener("click", async () => {
						if (await isMuted(videoPlayer)) {
							unmuteVideo(videoPlayer);
							if (muteIconSpan) muteIconSpan.innerHTML = await muteSvg;
						} else {
							muteVideo(videoPlayer);
							if (muteIconSpan) muteIconSpan.innerHTML = await unmuteSvg;
						}
					});
				}
			}

			function playVideo(videoPlayer) {
				if (videoPlayer.play) {
					if (type === "mp4") {
						videoPlayer.load();
					}
					videoPlayer.play();
				} else if (videoPlayer.playVideo) {
					videoPlayer.playVideo();
				}

				// Hide play button when video starts playing
				if (playButton) {
					playButton.classList.remove('!opacity-100');
				}
				if (muteButton) {
					muteButton.classList.remove('!opacity-100');
				}
			}

			function pauseVideo(videoPlayer) {
				if (videoPlayer.pause) {
					videoPlayer.pause();
				} else if (videoPlayer.pauseVideo) {
					videoPlayer.pauseVideo();
				}

				// Show play button when video is paused
				if (playButton) {
					playButton.classList.add('!opacity-100');
				}
				if (muteButton) {
					muteButton.classList.add('!opacity-100');
				}
			}

			async function isPlaying(videoPlayer) {
				if (videoPlayer.getPlayerState) {
					return videoPlayer.getPlayerState() === 1;
				} else if (videoPlayer.getPaused) {
					try {
						const isPaused = await videoPlayer.getPaused();
						return !isPaused;
					} catch (e) {
						return false;
					}
				} else {
					return !videoPlayer.paused;
				}
			}

			function muteVideo(videoPlayer) {
				if (videoPlayer.mute) {
					videoPlayer.mute();
				} else if (videoPlayer.setVolume) {
					videoPlayer.setVolume(0);
				} else {
					videoPlayer.muted = true;
				}
			}

			function unmuteVideo(videoPlayer) {
				if (videoPlayer.unMute) {
					videoPlayer.unMute();
				} else if (videoPlayer.setVolume) {
					videoPlayer.setVolume(1);
				} else {
					videoPlayer.muted = false;
				}
			}

			async function isMuted(videoPlayer) {
				if (videoPlayer.isMuted) {
					return videoPlayer.isMuted();
				} else if (videoPlayer.getVolume) {
					try {
						const volume = await videoPlayer.getVolume();
						return volume === 0;
					} catch (e) {
						return true;
					}
				} else {
					return videoPlayer.muted;
				}
			}

		});
	}
}